import { useTranslation } from "react-i18next";
import { formatDay, getInitials } from "utils/helpers";

interface FeedbackLike {
  summary?: string;
  note?: string;
  given_by?: string;
  role?: string;
  feedback_type?: string;
  created_at?: string;
}

interface OfficialFeedbackCardProps {
  /** The list of respond-back feedbacks (any of the per-module field names). */
  feedbacks?: FeedbackLike[] | null;
  /** Which feedback_type to surface. Defaults to the respond-back "Review". */
  feedbackType?: string;
  className?: string;
}

/**
 * Government-style "Official Response" card shown to an external user / stakeholder
 * when their request has been responded back. Reused across every request type.
 */
export default function OfficialFeedbackCard({
  feedbacks,
  feedbackType = "Review",
  className = "",
}: OfficialFeedbackCardProps) {
  const { t } = useTranslation();

  const matching = (feedbacks ?? []).filter(
    (f) => f?.feedback_type === feedbackType,
  );
  const feedback = matching.length ? matching[matching.length - 1] : null;
  if (!feedback) return null;

  const message = feedback.summary || feedback.note || "";
  if (!message) return null;

  return (
    <div
      className={`mb-4 rounded-xl overflow-hidden border border-[#9B0505]/25 shadow-md bg-white ${className}`}
    >
      {/* Official header bar */}
      <div className="flex items-center gap-3 px-5 py-3 bg-gradient-to-r from-[#7a1f1f] to-[#9B0505] text-white">
        <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.8}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 3l7 3v5c0 4.418-3.134 8.165-7 9-3.866-.835-7-4.582-7-9V6l7-3z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9.5 12l1.8 1.8L15 10"
            />
          </svg>
        </div>
        <div className="leading-tight">
          <p className="font-semibold text-sm">
            {t("OfficialResponse") || "Official Response"}
          </p>
          <p className="text-[11px] text-white/80">
            {t("OfficeOfTheAttorneyGeneral") || "Office of the Attorney General"}
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="px-5 py-4">
        <div className="flex items-center justify-between mb-3">
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold border border-amber-200">
            {t("ResponseRequested") || "Response Requested"}
          </span>
          {feedback.created_at && (
            <span className="text-xs text-gray-400">
              {formatDay(feedback.created_at).rest}
            </span>
          )}
        </div>
        <div className="border-l-4 border-[#9B0505]/40 pl-4">
          <p className="text-[#374151] text-sm leading-relaxed whitespace-pre-wrap">
            {message}
          </p>
        </div>
        {feedback.given_by && (
          <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#9B0505]/10 text-[#9B0505] flex items-center justify-center text-xs font-bold">
              {getInitials(feedback.given_by)}
            </div>
            <div className="leading-tight">
              <p className="text-xs font-semibold text-gray-700">
                {feedback.given_by}
              </p>
              {feedback.role && (
                <p className="text-[11px] text-gray-400 capitalize">
                  {feedback.role}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
